/**
 * Cross-region DM home-region rule. Phase 2 multi-region (WS-B4).
 *
 * For a DM between two users in different home regions, both clients need
 * to agree — without coordination — on which region's Postgres holds the
 * authoritative DM data. The rule:
 *
 * **Lower userId wins.** The user whose id sorts lexically smaller
 * "owns" the DM. Their `home_region` becomes the DM channel's region.
 *
 * Properties:
 *
 * - **Deterministic**: same pair of userIds always yields the same answer.
 * - **Stateless**: no server lookup, no race condition. Either client can
 *   compute it before initiating the create.
 * - **Stable across migrations**: even if a user later moves their
 *   home_region, existing DMs keep their original region. Only new DMs
 *   get the new value.
 *
 * For self-DMs (Notes channel — actor === other), region is just the
 * actor's home_region.
 */

/**
 * Supported regions. Mirrors `server/geo-ip.ts:Region` so client code can
 * reason about region values without depending on server-side geo-IP code.
 */
export type Region = "us" | "eu";

/**
 * Compute a DM channel's home region from the two participants' identities
 * and home regions.
 *
 * Pass `null`/`undefined` for either home_region to fall back to 'us' (the
 * post-2026-05-06 cutover default). This handles the transition window
 * where some pre-WS-B2 users haven't been bootstrapped yet.
 *
 * @param actor — the user initiating the DM
 * @param other — the recipient (or `null` for a self-DM / Notes channel)
 */
export function computeDmRegion(
  _actor: { id: string; home_region?: string | null },
  _other: { id: string; home_region?: string | null } | null,
): Region {
  // Single-region beta (2026-05-18 cutover): US Ashburn is the sole
  // authority for ALL data. DMs no longer have per-row region
  // semantics. Returns 'us' unconditionally.
  //
  // Function signature preserved for forward-compatibility — when
  // multi-region returns (post-EU-customer-demand), re-introduce the
  // lower-userId-wins logic. See
  // .claude/plans/look-at-this-issue-imperative-nygaard.md Phase 1f.
  return "us";
}
